export type HealthStatus =
  | 'connected'
  | 'requiresSetup'
  | 'waitlisted'
  | 'failure'
  | 'connected_with_errors'
  | 'no_jobs_found';

/**
 * Compute integration health status from job results.
 * Uses consecutive failure threshold: integration is only marked as 'failure'
 * when there are 3+ failed jobs with zero passing jobs (all recent jobs failed).
 * This prevents transient failures from marking the integration as unhealthy.
 *
 * Root job failure (rootJobFailed) is immediately definitive and aligns with
 * the backend n=1 threshold for integration root jobs.
 */
export function computeHealthStatus(
  integrationValue: string | undefined,
  jobStatus:
    | {
        failedJobsCount: number;
        passedJobsCount: number;
        rootJobFailed?: boolean;
      }
    | undefined
): HealthStatus {
  if (integrationValue === 'setup') return 'requiresSetup';
  if (integrationValue === 'waitlisted') return 'waitlisted';
  if (!jobStatus) return 'no_jobs_found';

  const { failedJobsCount, passedJobsCount, rootJobFailed } = jobStatus;

  // Root integration job failure is immediately definitive (aligns with backend n=1)
  if (rootJobFailed) return 'failure';

  // Consecutive failure threshold: 3+ failures with no passes = broken
  if (failedJobsCount >= 3 && passedJobsCount === 0) return 'failure';

  // Any failures but below threshold or mixed with passes = partial issues
  if (failedJobsCount > 0) return 'connected_with_errors';

  // No failures = healthy
  return 'connected';
}
